import classNames from 'classnames';
import classnames from 'classnames';
import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { Msg, UserNotification } from 'state/notifications/notification.model';
import { isHighlightedContent } from 'state/notifications/notification.model';

export type NotificationProps = Readonly<
  {
    onDismiss: (id: string) => void;
    className?: string;
  } & UserNotification
>;

function useConvertMsgToText(msg?: Msg): string {
  const { formatMessage } = useIntl();
  if (isHighlightedContent(msg)) {
    msg = msg.content;
  }
  if (!msg) {
    return '';
  }
  if (typeof msg === 'string') {
    return msg as string;
  }
  return formatMessage({ id: msg.id }, msg.values);
}

export const NotificationMsg: FC<{ msg: Msg; className?: string }> = ({ className, msg }) => {
  const text = useConvertMsgToText(msg);
  const highLightedContent = useMemo(() => (isHighlightedContent(msg) ? msg : undefined), [msg]);

  return (
    <span className={classNames('justify-content-start text-no-wrap d-flex', className)}>
      {highLightedContent?.icon && (
        <em className="icon" color={highLightedContent?.severity}>
          icon
        </em>
      )}
      {highLightedContent?.isHtml ? (
        <span
          className={classNames('text-truncate', {
            [`text-${highLightedContent.severity}`]: !!highLightedContent?.severity,
          })}
          dangerouslySetInnerHTML={{ __html: text }}
        ></span>
      ) : (
        <span
          className={classNames({
            [`text-${highLightedContent?.severity}`]: !!highLightedContent?.severity,
          })}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export const Notification: FC<NotificationProps> = ({
  className,
  title,
  content,
  details,
  level,
  closeAutomatically,
  onDismiss,
  minWidth,
  id,
}) => {
  const onClose = useCallback(() => {
    onDismiss(id);
  }, [id, onDismiss]);
  useEffect(() => {
    if (closeAutomatically) {
      const timeoutInMs = level === 'danger' || level === 'warning' ? 10_000 : 5_000;

      const idTimeout = setTimeout(() => onClose(), timeoutInMs);
      return () => clearTimeout(idTimeout);
    }
  }, [closeAutomatically, level, onClose]);

  return (
    <div
      className={classnames(`toast toast-${level} fade show mb-2`, className)}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      style={{ minWidth }}
    >
      <div className="toast-header">
        <strong className="me-auto">
          <NotificationMsg msg={title} />
        </strong>
        <button
          type="button"
          className="ms-2 mb-1 btn-close"
          data-dismiss="toast"
          aria-label="Close"
          onClick={onClose}
        />
      </div>

      <div className="toast-body flex-column d-flex">
        {!!content && content.map(c => <NotificationMsg key={c.toString()} msg={c} />)}

        {!!details && (
          <div className={classnames('px-4 py-2 my-3 border bg-lvl2', `border-${level} text-${level}`)}>
            {details.map(({ id, values }) => (
              <FormattedMessage
                key={id}
                id={id}
                values={{
                  ...values,
                  h: (chunks: ReactNode[]) => (
                    <div className="d-flex display-5 pb-2 justify-content-between align-items-center">{chunks}</div>
                  ),
                  sm: (chunks: ReactNode[]) => <pre className="fs-12 d-inline text-wrap">{chunks}</pre>,
                  badge: (chunks: ReactNode[]) => (
                    <div className={classNames(`badge badge-discreet-${level} m-2`)}>{chunks}</div>
                  ),
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
